import { useState, useEffect, useRef } from "react";
import { UploadCloud, CheckCircle2, AlertTriangle, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fdate, eur2 } from "@/lib/format";

const NONE = "__none__";

export default function FuelImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setFile(null); setPreview(null); setAssignments({}); setBusy(false); }
  }, [open]);

  const analyze = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/fuel/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.kind === "pdf") {
        const init = {};
        data.supports.forEach((s) => { if (s.matched_vehicle_id) init[s.card_number] = s.matched_vehicle_id; });
        setAssignments(init);
      }
    } catch (e) { toast.error(apiError(e)); setFile(null); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    if (preview.kind === "pdf") fd.append("assignments", JSON.stringify(assignments));
    try {
      const { data } = await api.post("/fuel/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.kind === "pdf") toast.success(`${data.inserted} plein(s) importé(s) — période ${data.period_label}`);
      else toast.success(`${data.inserted} ligne(s) importée(s) — période ${data.period_label}`);
      onOpenChange(false); onDone && onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const Stat = ({ icon: I, val, label, cls }) => (
    <div className={`rounded-xl border p-3 ${cls}`}><I className="h-4 w-4" /><div className="mt-1 font-heading text-xl font-bold">{val}</div><div className="text-[11px]">{label}</div></div>
  );

  const isPdf = preview?.kind === "pdf";
  const matchedCount = isPdf ? Object.values(assignments).filter(Boolean).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UploadCloud className="h-5 w-5" />Importer le fichier mensuel</DialogTitle></DialogHeader>

        {!preview && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); analyze(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`mt-2 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-[#A5894E] bg-[#F4EFE3]" : "border-[#E2E8F0] hover:border-[#001A3B]/40"}`}
            data-testid="fuel-dropzone"
          >
            <UploadCloud className="mb-3 h-10 w-10 text-[#001A3B]" />
            <p className="font-medium text-[#18221F]">Glissez-déposez le fichier PDF, Excel ou CSV</p>
            <p className="mt-1 text-xs text-[#8695AB]">Facture carte carburant (PDF, rapprochement par nom) ou tableau Excel/CSV (par immatriculation)</p>
            {busy && <p className="mt-3 text-sm text-[#001A3B]">Analyse en cours…</p>}
            <input ref={inputRef} type="file" accept=".pdf,.xlsx,.csv" className="hidden" onChange={(e) => analyze(e.target.files[0])} data-testid="fuel-file-input" />
          </div>
        )}

        {preview?.already_imported && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4" />Ce fichier a déjà été importé le {fdate(preview.already_imported_at)}. L'import est bloqué pour éviter les doublons.
          </div>
        )}

        {preview && (
          <div className="py-2">
            <div className="mb-3 flex items-center justify-between rounded-lg bg-[#EEF2F8] px-4 py-2">
              <span className="text-sm text-[#4A5568]">Période détectée</span>
              <span className="font-heading font-semibold text-[#001A3B]">{preview.period_label || "—"} <span className="text-xs font-normal text-[#8695AB]">({fdate(preview.period_start)} → {fdate(preview.period_end)})</span></span>
            </div>

            {isPdf ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat icon={CheckCircle2} val={preview.totals.new} label="Nouveaux pleins" cls="border-emerald-200 bg-emerald-50 text-emerald-800" />
                  <Stat icon={AlertTriangle} val={preview.totals.duplicates} label="Doublons" cls="border-amber-200 bg-amber-50 text-amber-800" />
                  <Stat icon={FileText} val={eur2(preview.totals.fuel_amount)} label="Total carburant" cls="border-[#C7D5EC] bg-[#EEF2F8] text-[#001A3B]" />
                  <Stat icon={XCircle} val={preview.totals.unmatched} label="Non rapprochés" cls="border-orange-200 bg-orange-50 text-orange-800" />
                </div>
                <p className="mt-2 text-xs text-[#8695AB]">{preview.totals.supports} conducteur(s)/carte(s) · {preview.totals.fuel_liters} L de carburant · {eur2(preview.totals.toll_amount)} de péages/services</p>

                <div className="mt-4 overflow-hidden rounded-lg border border-[#E2E8F0]">
                  <div className="overflow-x-auto"><table className="w-full text-xs">
                    <thead><tr className="bg-[#F5F7FA] text-left text-[#4A5568]">{["Conducteur (facture)", "Carte", "Pleins", "Litres", "Montant", "Péages", "Véhicule à affecter"].map((h) => <th key={h} className="px-2 py-1.5 font-semibold">{h}</th>)}</tr></thead>
                    <tbody>{preview.supports.map((s) => (
                      <tr key={s.card_number + s.driver_name} className="border-t border-[#EDF0F5]" data-testid="fuel-support-row">
                        <td className="px-2 py-1.5 font-medium text-[#18221F]">{s.driver_name}</td>
                        <td className="px-2 py-1.5 font-mono text-[#8695AB]">{s.card_number}</td>
                        <td className="px-2 py-1.5">{s.fuel_count}{s.duplicates > 0 && <span className="ml-1 text-amber-600">(-{s.duplicates})</span>}</td>
                        <td className="px-2 py-1.5">{s.fuel_liters} L</td>
                        <td className="px-2 py-1.5">{eur2(s.fuel_amount)}</td>
                        <td className="px-2 py-1.5">{s.toll_count > 0 ? eur2(s.toll_amount) : "—"}</td>
                        <td className="px-2 py-1.5">
                          <Select value={assignments[s.card_number] || NONE} onValueChange={(v) => setAssignments((a) => ({ ...a, [s.card_number]: v === NONE ? "" : v }))}>
                            <SelectTrigger className="h-8 w-56 bg-white text-xs" data-testid="fuel-support-vehicle"><SelectValue placeholder="Non rapproché" /></SelectTrigger>
                            <SelectContent><SelectItem value={NONE}>— Non rapproché —</SelectItem>{preview.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>
                <p className="mt-2 text-xs text-[#8695AB]">{matchedCount} conducteur(s) rapproché(s) à un véhicule. Les non rapprochés seront enregistrés sans véhicule et pourront être affectés plus tard (le rapprochement par carte est mémorisé).</p>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat icon={CheckCircle2} val={preview.new} label="Nouvelles lignes" cls="border-emerald-200 bg-emerald-50 text-emerald-800" />
                  <Stat icon={AlertTriangle} val={preview.duplicates} label="Doublons" cls="border-amber-200 bg-amber-50 text-amber-800" />
                  <Stat icon={XCircle} val={preview.unmatched.length} label="Non rapprochés" cls="border-orange-200 bg-orange-50 text-orange-800" />
                  <Stat icon={AlertTriangle} val={preview.errors.length} label="Erreurs" cls="border-red-200 bg-red-50 text-red-800" />
                </div>
                <p className="mt-2 text-xs text-[#8695AB]">{preview.rows_total} ligne(s) au total · {preview.ignored} ligne(s) non-carburant ignorée(s) · {preview.vehicles_concerned} véhicule(s) concerné(s)</p>
                {preview.unmatched.length > 0 && (
                  <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50/50 p-2 text-xs text-orange-800">
                    <span className="font-semibold">Immatriculations absentes de la flotte :</span> {preview.unmatched.map((u) => u.raw).join(", ")}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {preview && !preview.already_imported && <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>Changer de fichier</Button>}
          {preview && !preview.already_imported && (
            <Button onClick={commit} disabled={busy || (isPdf ? preview.totals.new === 0 : preview.new === 0)} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="fuel-import-confirm">
              {busy ? "Import…" : `Confirmer l'import (${isPdf ? preview.totals.new : preview.new})`}
            </Button>
          )}
          {(!preview || preview.already_imported) && <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
